/**
 * Shared HTTP client for web apps.
 *
 * Implements: ApiError normalization, token injection, 401 global handling,
 * request signing (REQUEST_SECURITY_PROTOCOL.md), server time offset sync.
 *
 * The client must be configured at app startup via configureClient() before
 * making any requests. This injects the token getter and 401 handler from
 * the app's auth store without creating a circular dependency between
 * packages/api-client and apps/admin.
 */

import type { ApiError } from '@aiptyg/types';
import { buildSecurityHeaders, updateServerTimeOffset } from './request-security';

// ── Configuration ─────────────────────────────────────────────────

interface ClientConfig {
  baseUrl: string;
  securityEnabled: boolean;
  anonymousSecret: string;
  getToken: () => string | null;
  onUnauthorized: () => void;
  onEvent: (event: ApiClientEvent) => void;
}

export interface ApiClientEvent {
  type: 'request_start' | 'request_success' | 'request_failure';
  method: string;
  path: string;
  requestId: string;
  status?: number;
  duration?: number;
  message?: string;
  signed: boolean;
  hasAuth: boolean;
}

let _config: ClientConfig = {
  baseUrl: '',
  securityEnabled: false,
  anonymousSecret: '',
  getToken: () => null,
  onUnauthorized: () => {},
  onEvent: () => {},
};

export function configureClient(config: Partial<ClientConfig>): void {
  _config = { ..._config, ...config };
}

// ── Constants ─────────────────────────────────────────────────────

const DEFAULT_TIMEOUT = 15_000;

// Paths that must NOT receive an Authorization header
const ANON_PREFIXES = ['/v1/auth/', '/v1/public/'];

function isAnonPath(path: string): boolean {
  return ANON_PREFIXES.some((p) => path.startsWith(p));
}

function stringField(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function extractApiErrorMessage(data?: Record<string, unknown>): string {
  const errorPayload = data?.error;
  if (typeof errorPayload === 'string') {
    return stringField(errorPayload) ?? 'unknown_error';
  }
  if (typeof errorPayload === 'object' && errorPayload !== null) {
    const nested = errorPayload as Record<string, unknown>;
    return stringField(nested.code)
      ?? stringField(nested.message)
      ?? stringField(data?.code)
      ?? 'unknown_error';
  }
  return stringField(data?.code) ?? 'unknown_error';
}

// ── Client-generated request ID ───────────────────────────────────

function generateRequestId(): string {
  const h = '0123456789abcdef';
  const seg = [8, 4, 4, 4, 12];
  return seg
    .map((len) =>
      Array.from({ length: len }, () => h[Math.floor(Math.random() * 16)]).join(''),
    )
    .join('-');
}

// ── Shared request helper ─────────────────────────────────────────

interface RequestOptions {
  timeout?: number;
  anonymous?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  const requestId = generateRequestId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-Id': requestId,
  };

  // Token injection — skip for auth/public paths or explicit anonymous
  let token: string | null = null;
  if (!options?.anonymous && !isAnonPath(path)) {
    token = _config.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Serialize body once so security hash and fetch use the same bytes
  const rawBody = body ? JSON.stringify(body) : undefined;

  // Request security headers
  if (_config.securityEnabled) {
    const secret = token ?? _config.anonymousSecret;
    if (!secret) {
      throw { status: 0, message: 'missing_request_signature_secret' } satisfies ApiError;
    }
    const secHeaders = await buildSecurityHeaders(method, path, rawBody ?? '', secret);
    Object.assign(headers, secHeaders);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options?.timeout ?? DEFAULT_TIMEOUT);
  const hasSec = _config.securityEnabled;
  const hasAuth = Boolean(token);
  const startMs = Date.now();
  _config.onEvent({
    type: 'request_start',
    method,
    path,
    requestId,
    signed: hasSec,
    hasAuth,
  });

  let res: Response;
  try {
    res = await fetch(`${_config.baseUrl}${path}`, {
      method,
      headers,
      body: rawBody,
      signal: controller.signal,
    });
  } catch {
    _config.onEvent({
      type: 'request_failure',
      method,
      path,
      requestId,
      duration: Date.now() - startMs,
      message: 'network_error',
      signed: hasSec,
      hasAuth,
    });
    throw { status: 0, message: 'network_error' } satisfies ApiError;
  } finally {
    clearTimeout(timer);
  }

  // Maintain server time offset for signature timestamps
  const serverTime = res.headers.get('X-Server-Time');
  if (serverTime) {
    const ts = Number(serverTime);
    if (!Number.isNaN(ts)) updateServerTimeOffset(ts);
  }

  let data: Record<string, unknown> | undefined;
  const rawResponse = await res.text();
  if (rawResponse.trim()) {
    try {
      data = JSON.parse(rawResponse) as Record<string, unknown>;
    } catch {
      _config.onEvent({
        type: 'request_failure',
        method,
        path,
        requestId,
        status: res.status,
        duration: Date.now() - startMs,
        message: 'invalid_response',
        signed: hasSec,
        hasAuth,
      });
      throw { status: res.status, message: 'invalid_response' } satisfies ApiError;
    }
  }

  if (!res.ok) {
    const errMsg = extractApiErrorMessage(data);
    _config.onEvent({
      type: 'request_failure',
      method,
      path,
      requestId,
      status: res.status,
      duration: Date.now() - startMs,
      message: errMsg,
      signed: hasSec,
      hasAuth,
    });
    // 401 → notify auth store to clear session
    if (res.status === 401 && !isAnonPath(path)) {
      _config.onUnauthorized();
    }
    throw { status: res.status, message: errMsg } satisfies ApiError;
  }

  _config.onEvent({
    type: 'request_success',
    method,
    path,
    requestId,
    status: res.status,
    duration: Date.now() - startMs,
    signed: hasSec,
    hasAuth,
  });
  return data as T;
}

// ── Public methods ────────────────────────────────────────────────

export function get<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>('GET', path, undefined, options);
}

export function post<T>(
  path: string,
  body: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('POST', path, body, options);
}

export function put<T>(
  path: string,
  body: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('PUT', path, body, options);
}

export function patch<T>(
  path: string,
  body: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('PATCH', path, body, options);
}

export function del<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>('DELETE', path, undefined, options);
}
